import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Issuer } from '../types';

interface IssuerPickerProps {
  issuers: Issuer[];
  selected: string; // issuer ID (full URL form)
  onSelect: (issuerId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

function IssuerAvatar({ issuer, size = 'md' }: { issuer: Issuer; size?: 'sm' | 'md' | 'lg' }) {
  const dim = size === 'sm' ? 'h-10 w-10' : size === 'lg' ? 'h-14 w-14' : 'h-10 w-10';
  if (issuer.image) {
    return (
      <img
        src={issuer.image}
        alt={`${issuer.name} logo`}
        className={`${dim} rounded-lg object-cover bg-gray-50 border border-gray-200 flex-shrink-0`}
      />
    );
  }
  const initial = issuer.name.charAt(0).toUpperCase();
  return (
    <div className={`${dim} rounded-lg bg-primary-50 border border-primary-100 flex items-center justify-center text-primary-700 font-semibold flex-shrink-0`}>
      {initial}
    </div>
  );
}

export function IssuerPicker({ issuers, selected, onSelect, placeholder, disabled }: IssuerPickerProps) {
  const { t } = useTranslation('dashboard');
  const placeholderText = placeholder ?? t('badges.selectIssuer', 'Select an issuer...');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedIssuer = useMemo(
    () => issuers.find(i => i.id === selected),
    [issuers, selected]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return issuers;
    const q = search.toLowerCase();
    return issuers.filter(
      i => i.name.toLowerCase().includes(q) ||
           i.email?.toLowerCase().includes(q) ||
           i.url?.toLowerCase().includes(q)
    );
  }, [issuers, search]);

  function handleSelect(issuer: Issuer) {
    onSelect(issuer.id);
    setOpen(false);
    setSearch('');
  }

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="open-issuer-picker"
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        className="w-full flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left text-sm transition-colors hover:border-gray-300 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-gray-200"
      >
        {selectedIssuer ? (
          <>
            <IssuerAvatar issuer={selectedIssuer} size="md" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 truncate">{selectedIssuer.name}</p>
              <p className="text-xs text-gray-500 truncate">{selectedIssuer.email}</p>
            </div>
          </>
        ) : (
          <span className="text-gray-400">{placeholderText}</span>
        )}
        <svg className="h-4 w-4 text-gray-400 flex-shrink-0 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => { setOpen(false); setSearch(''); }} />
          <div className="relative z-10 w-full max-w-2xl max-h-[80vh] rounded-2xl bg-white shadow-xl flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Select Issuer</h3>
                <button onClick={() => { setOpen(false); setSearch(''); }} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
              </div>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('badges.issuerPickerSearchPlaceholder', 'Search by name, email, or URL...')}
                autoFocus
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {filtered.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400">
                  {issuers.length === 0 ? 'No verified issuers. Verify an issuer first.' : 'No issuers match your search.'}
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {filtered.map(issuer => {
                    const isSelected = issuer.id === selected;
                    return (
                      <button
                        key={issuer.id}
                        data-testid={`issuer-card-${issuer.id.split('/').pop() || issuer.id}`}
                        onClick={() => handleSelect(issuer)}
                        className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-all hover:shadow-md ${
                          isSelected
                            ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <IssuerAvatar issuer={issuer} size="lg" />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-semibold truncate ${isSelected ? 'text-primary-700' : 'text-gray-900'}`}>
                            {issuer.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {issuer.email}
                          </p>
                          <p className="text-xs text-gray-400 truncate mt-1">
                            {issuer.url}
                          </p>
                        </div>
                        {isSelected && (
                          <svg className="h-5 w-5 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
